import { useCallback, useEffect, useRef, useState } from 'react';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { useAppSelector } from 'hooks/typedHooks';
import { IPaginatedItem } from 'containers/product-catalog/items/items.model';
import { useLazyGetCatalogItemsQuery } from 'containers/product-catalog/items/api';

export interface GetItemsQueryParams {
  isSelected: boolean;
  taxId: number;
  categoryId: number;
  search: string;
  includeTax?: boolean;
}

export const initialQueryParamsState: GetItemsQueryParams = {
  isSelected: null,
  taxId: null,
  categoryId: null,
  search: null,
  includeTax: null
};

export interface IUsePaginatedGetCatalogItemsQueryConfig {
  isSelected?: boolean;
  taxId?: number;
  categoryId?: number;
  includeTax?: boolean;
}

// TODO replace this with the paginated hook used in /items listing page
export const usePaginatedGetCatalogItemsQuery = (config: IUsePaginatedGetCatalogItemsQueryConfig = null) => {
  const [list, setList] = useState<IPaginatedItem[]>([]);
  const [isMounted, setIsMounted] = useState(false);
  const [queryParams, setQueryParams] = useState<GetItemsQueryParams>({ ...initialQueryParamsState, ...config });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [totalListCount, setTotalListCount] = useState<number>(0);
  const page = useRef(0);
  const [listQueryCall, listQueryState] = useLazyGetCatalogItemsQuery();
  const currentTeam = useAppSelector(selectCurrentTeam);

  const isInitListCall: boolean = page.current === 0;

  useEffect(() => {
    if (!listQueryState?.isFetching && listQueryState.isSuccess) {
      const totalCount = listQueryState?.data?.meta?.totalCount;
      const listItems = listQueryState?.data?.data ?? [];
      const updatedList = isInitListCall ? listItems : [...list, ...listItems];
      const listItemsLeftToFetch = totalCount ? +totalCount - updatedList.length : 0;

      setHasMore(listItemsLeftToFetch > 0);
      setList(updatedList);
      if (page.current === 1) {
        setTotalListCount(totalCount ?? 0);
      }
      setIsLoading(false);
    }
  }, [listQueryState?.isFetching]);

  useEffect(() => {
    if (!listQueryState?.isFetching && listQueryState?.isError) {
      setIsError(true);
      setIsLoading(false);
      setHasMore(false);
    }
  }, [listQueryState?.isError]);

  const resetListAndLoadMore = () => {
    page.current = 0;
    setList([]);
    loadMore();
  };

  useEffect(() => {
    if (isMounted) {
      resetListAndLoadMore();
    } else {
      setIsMounted(true);
    }
  }, [queryParams]);

  const loadMore = useCallback(() => {
    setIsLoading(true);
    if (currentTeam?.id) {
      const remainingParams = queryParams;

      try {
        listQueryCall({
          teamId: currentTeam?.id,
          limit: 10,
          page: page.current,
          ...remainingParams
        }).unwrap();
      } catch (err) {
        setIsError(true);
      }
    }
    page.current = page.current + 1;
  }, [currentTeam?.id, page.current, queryParams]);

  const resetRecallQuery = () => {
    resetListAndLoadMore();
  };

  const resetQuery = () => {
    page.current = 0;
    setList([]);
    setQueryParams(initialQueryParamsState);
    setIsMounted(false);
    setIsError(false);
    setHasMore(true);
    setTotalListCount(0);
  };

  return {
    isLoading: isLoading,
    isFetching: listQueryState?.isFetching,
    totalListCount,
    list,
    isError,
    loadMore,
    resetRecallQuery,
    resetQuery,
    hasMore,
    setQueryParams,
    queryParams,
    isInitListCall,
    isLoadingSuccess: listQueryState.isSuccess,
    apiErrorCode: listQueryState?.error?.code
  };
};
